import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import {
  DAMAGE_PROFILES,
  WEAPONS,
  WEAPON_PRESETS,
  createDamageWorld,
  type DamageWorld,
  type LastStampCall,
  type WeaponKind,
} from '../structures/siege/damage';
import { energyToSlider, sliderToEnergy, snapEnergy } from '../services/energy';
import { useViewportWidth } from '../hooks/useViewportWidth';
import '../components/MobileLabChrome.css';
import './DamageLab.css';

const MOBILE_BREAKPOINT = 768;
const DEFAULT_CAM = { px: 1.35, py: 1.55, pz: 5.35, tx: 0, ty: 1.25, tz: 0 };

interface DamageLabState {
  ready: boolean;
  stamps: number;
  removed: number;
  rim: number;
  spalled: number;
  cracked: number;
  frame: number;
  pocks: number;
  mergedZones: number;
  fps: number;
  weapon: WeaponKind;
  stains: number;
  scorch: number;
  debris: number;
  backFace: number;
}

interface DamageLabProbe {
  stampAt: (
    x: number,
    y: number,
    z: number,
    dx: number,
    dy: number,
    dz: number,
    nx?: number,
    ny?: number,
    nz?: number,
  ) => void;
  setWeapon: (kind: WeaponKind) => void;
  setEnergy: (e: number) => void;
  reset: () => void;
  setCamera?: (px: number, py: number, pz: number, tx: number, ty: number, tz: number) => void;
  getCamera?: () => {
    x: number;
    y: number;
    z: number;
    tx: number;
    ty: number;
    tz: number;
    azimuth: number;
    polar: number;
  } | null;
  setLedgerView?: (on: boolean) => void;
}

declare global {
  interface Window {
    __damageLabState?: DamageLabState;
    __damageLabProbe?: DamageLabProbe;
  }
}

function writeState(patch: Partial<DamageLabState>): void {
  const prev = window.__damageLabState;
  window.__damageLabState = {
    ready: patch.ready ?? prev?.ready ?? false,
    stamps: patch.stamps ?? prev?.stamps ?? 0,
    removed: patch.removed ?? prev?.removed ?? 0,
    rim: patch.rim ?? prev?.rim ?? 0,
    spalled: patch.spalled ?? prev?.spalled ?? 0,
    cracked: patch.cracked ?? prev?.cracked ?? 0,
    frame: patch.frame ?? prev?.frame ?? 0,
    pocks: patch.pocks ?? prev?.pocks ?? 0,
    mergedZones: patch.mergedZones ?? prev?.mergedZones ?? 0,
    fps: patch.fps ?? prev?.fps ?? 0,
    weapon: patch.weapon ?? prev?.weapon ?? 'cannon',
    stains: patch.stains ?? prev?.stains ?? 0,
    scorch: patch.scorch ?? prev?.scorch ?? 0,
    debris: patch.debris ?? prev?.debris ?? 0,
    backFace: patch.backFace ?? prev?.backFace ?? 0,
  };
}

function LabScene({
  worldRef,
  energyRef,
  weaponRef,
  controlsRef,
  ledgerCanvasRef,
  showLedgerRef,
  onHud,
  onStamp,
}: {
  worldRef: MutableRefObject<DamageWorld | null>;
  energyRef: MutableRefObject<number>;
  weaponRef: MutableRefObject<WeaponKind>;
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  ledgerCanvasRef: MutableRefObject<HTMLCanvasElement | null>;
  showLedgerRef: MutableRefObject<boolean>;
  onHud: (s: {
    fps: number;
    stamps: number;
    removed: number;
    rim: number;
    spalled: number;
    cracked: number;
    pocks: number;
    frame: number;
    ready: boolean;
    stains: number;
    scorch: number;
    debris: number;
    backFace: number;
    lastCall: LastStampCall | null;
  }) => void;
  onStamp?: () => void;
}) {
  const { gl, scene, camera } = useThree();
  const raycaster = useRef(new THREE.Raycaster());
  const ndc = useRef(new THREE.Vector2());
  const fpsAcc = useRef({ frames: 0, last: performance.now(), fps: 0 });
  const lastBridge = useRef(0);
  const lightRef = useRef<THREE.DirectionalLight>(null);

  useLayoutEffect(() => {
    const world = createDamageWorld(gl, {});
    world.attach(scene);
    worldRef.current = world;
    const st = world.getStats();
    writeState({
      ready: world.ready,
      stamps: 0,
      removed: 0,
      rim: 0,
      spalled: 0,
      cracked: 0,
      frame: st.frame,
      pocks: 0,
      mergedZones: 0,
      stains: 0,
      scorch: 0,
      debris: 0,
      backFace: 0,
    });
    return () => {
      world.detach();
      world.dispose();
      worldRef.current = null;
    };
  }, [gl, scene, worldRef]);

  const fireAtClient = useCallback(
    (clientX: number, clientY: number) => {
      const world = worldRef.current;
      if (!world?.ready) return;
      const el = gl.domElement;
      const rect = el.getBoundingClientRect();
      ndc.current.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.current.setFromCamera(ndc.current, camera);
      const hits = raycaster.current.intersectObjects(world.getPickables(), false);
      if (hits.length === 0) return;
      const hit = hits[0];
      const worldN = new THREE.Vector3(0, 0, 1);
      if (hit.normal && hit.normal.lengthSq() > 1e-8) {
        worldN.copy(hit.normal).normalize();
      } else if (hit.face?.normal) {
        worldN.copy(hit.face.normal).transformDirection(hit.object.matrixWorld).normalize();
      }
      const dir = new THREE.Vector3().subVectors(hit.point, camera.position).normalize();
      const preset = WEAPON_PRESETS[weaponRef.current];
      world.stamp({
        position: [hit.point.x, hit.point.y, hit.point.z],
        direction: [dir.x, dir.y, dir.z],
        energy: energyRef.current,
        intensity: preset.intensity,
        blastFraction: preset.blastFraction,
        surfaceNormal: [worldN.x, worldN.y, worldN.z],
      });
      onStamp?.();
    },
    [camera, gl, energyRef, weaponRef, worldRef, onStamp],
  );

  useEffect(() => {
    const canvas = gl.domElement;
    let down: { x: number; y: number; t: number } | null = null;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      down = { x: e.clientX, y: e.clientY, t: performance.now() };
    };
    const onUp = (e: PointerEvent) => {
      if (!down || e.button !== 0) return;
      const dist = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      const dt = performance.now() - down.t;
      down = null;
      if (dist <= 5 && dt <= 400) fireAtClient(e.clientX, e.clientY);
    };
    const flushOrbitPointer = (event: PointerEvent) => {
      document.dispatchEvent(
        new PointerEvent('pointerup', {
          bubbles: true,
          cancelable: true,
          pointerId: event.pointerId,
          pointerType: event.pointerType,
          isPrimary: event.isPrimary,
          clientX: event.clientX,
          clientY: event.clientY,
          button: 0,
          buttons: 0,
        }),
      );
    };
    const onCancel = (event: PointerEvent) => {
      down = null;
      flushOrbitPointer(event);
    };
    const onContext = (e: MouseEvent) => e.preventDefault();
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    document.addEventListener('pointercancel', onCancel);
    canvas.addEventListener('contextmenu', onContext);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      document.removeEventListener('pointercancel', onCancel);
      canvas.removeEventListener('contextmenu', onContext);
    };
  }, [fireAtClient, gl]);

  useFrame((_, dt) => {
    const world = worldRef.current;
    if (world) world.step(dt);
    const acc = fpsAcc.current;
    acc.frames += 1;
    const now = performance.now();
    if (now - acc.last >= 400) {
      acc.fps = (acc.frames * 1000) / (now - acc.last);
      acc.frames = 0;
      acc.last = now;
    }
    const st = world?.getStats();
    writeState({
      ready: world?.ready ?? false,
      stamps: st?.stamps ?? 0,
      removed: st?.removed ?? 0,
      rim: st?.rim ?? 0,
      spalled: st?.spalled ?? 0,
      cracked: st?.cracked ?? 0,
      frame: st?.frame ?? 0,
      pocks: st?.pocks ?? 0,
      mergedZones: st?.mergedZones ?? 0,
      fps: acc.fps,
      stains: st?.stains ?? 0,
      scorch: st?.scorch ?? 0,
      debris: st?.debris ?? 0,
      backFace: st?.backFace ?? 0,
    });
    if (now - lastBridge.current >= 100) {
      lastBridge.current = now;
      onHud({
        fps: acc.fps,
        stamps: st?.stamps ?? 0,
        removed: st?.removed ?? 0,
        rim: st?.rim ?? 0,
        spalled: st?.spalled ?? 0,
        cracked: st?.cracked ?? 0,
        pocks: st?.pocks ?? 0,
        frame: st?.frame ?? 0,
        ready: world?.ready ?? false,
        stains: st?.stains ?? 0,
        scorch: st?.scorch ?? 0,
        debris: st?.debris ?? 0,
        backFace: st?.backFace ?? 0,
        lastCall: world?.lastStampCall() ?? null,
      });
      if (showLedgerRef.current && ledgerCanvasRef.current && world) {
        world.blitLedgerPreview(ledgerCanvasRef.current);
      }
    }
  });

  return (
    <>
      <color attach="background" args={['#08090c']} />
      <fog attach="fog" args={['#0a0b0e', 18, 42]} />
      <mesh renderOrder={-10}>
        <sphereGeometry args={[36, 24, 16]} />
        <shaderMaterial
          side={THREE.BackSide}
          depthWrite={false}
          fog={false}
          toneMapped={false}
          vertexShader={`
            varying vec3 vDir;
            void main() {
              vDir = position;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `}
          fragmentShader={`
            varying vec3 vDir;
            void main() {
              float h = normalize(vDir).y;
              vec3 top = vec3(0.028, 0.030, 0.036);
              vec3 hzCol = vec3(0.09, 0.082, 0.078);
              vec3 bot = vec3(0.035, 0.032, 0.030);
              vec3 col = mix(bot, hzCol, smoothstep(-0.20, 0.0, h));
              col = mix(col, top, smoothstep(0.04, 0.38, h));
              gl_FragColor = vec4(col, 1.0);
            }
          `}
        />
      </mesh>
      <hemisphereLight args={['#b8c0cc', '#1a1816', 0.52]} />
      <directionalLight
        ref={lightRef}
        position={[5.4, 7.2, 6.2]}
        intensity={1.22}
        color="#fff1d6"
      />
      <directionalLight position={[-2.4, 2.8, 7.2]} intensity={0.42} color="#ffe8d0" />
      <directionalLight position={[-2.2, 4.0, -3.8]} intensity={1.05} color="#d8e0ea" />
      <ambientLight intensity={0.22} />
      <OrbitControls
        ref={controlsRef}
        enableDamping={false}
        minPolarAngle={0.18}
        maxPolarAngle={Math.PI / 2 - 0.04}
        minDistance={1.6}
        maxDistance={16}
        target={[DEFAULT_CAM.tx, DEFAULT_CAM.ty, DEFAULT_CAM.tz]}
        makeDefault
      />
    </>
  );
}

export default function DamageLab({ onBack }: { onBack: () => void }) {
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < MOBILE_BREAKPOINT;

  const worldRef = useRef<DamageWorld | null>(null);
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const energyRef = useRef(2);
  const weaponRef = useRef<WeaponKind>('cannon');
  const ledgerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const showLedgerRef = useRef(false);

  const [weapon, setWeaponState] = useState<WeaponKind>('cannon');
  const [energy, setEnergy] = useState(2);
  const [showLedger, setShowLedger] = useState(false);
  const [apiOpen, setApiOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [hintVisible, setHintVisible] = useState(true);
  const [hud, setHud] = useState({
    fps: 0,
    stamps: 0,
    removed: 0,
    rim: 0,
    spalled: 0,
    cracked: 0,
    pocks: 0,
    frame: 0,
    ready: false,
    stains: 0,
    scorch: 0,
    debris: 0,
    backFace: 0,
    lastCall: null as LastStampCall | null,
  });

  energyRef.current = energy;
  weaponRef.current = weapon;
  showLedgerRef.current = showLedger;

  const dismissHint = useCallback(() => setHintVisible(false), []);

  useEffect(() => {
    if (!isMobile || !hintVisible) return;
    const id = window.setTimeout(() => setHintVisible(false), 4000);
    return () => window.clearTimeout(id);
  }, [isMobile, hintVisible]);

  useEffect(() => {
    writeState({
      ready: false,
      stamps: 0,
      removed: 0,
      rim: 0,
      spalled: 0,
      cracked: 0,
      frame: 0,
      pocks: 0,
      mergedZones: 0,
      fps: 0,
      weapon: 'cannon',
      stains: 0,
      scorch: 0,
      debris: 0,
      backFace: 0,
    });
    window.__damageLabProbe = {
      stampAt(x, y, z, dx, dy, dz, nx, ny, nz) {
        const world = worldRef.current;
        if (!world) return;
        const preset = WEAPON_PRESETS[weaponRef.current];
        world.stamp({
          position: [x, y, z],
          direction: [dx, dy, dz],
          energy: energyRef.current,
          intensity: preset.intensity,
          blastFraction: preset.blastFraction,
          surfaceNormal:
            nx !== undefined && ny !== undefined && nz !== undefined
              ? [nx, ny, nz]
              : [0, 0, 1],
        });
      },
      setWeapon(kind) {
        const next: WeaponKind = WEAPONS.includes(kind) ? kind : 'cannon';
        weaponRef.current = next;
        setWeaponState(next);
        writeState({ weapon: next });
      },
      setEnergy(e) {
        const v = snapEnergy(e);
        setEnergy(v);
        energyRef.current = v;
      },
      reset() {
        worldRef.current?.reset();
        writeState({
          ready: worldRef.current?.ready ?? false,
          stamps: 0,
          removed: 0,
          rim: 0,
          spalled: 0,
          cracked: 0,
          frame: 0,
          pocks: 0,
          mergedZones: 0,
          stains: 0,
          scorch: 0,
          debris: 0,
          backFace: 0,
        });
      },
      setCamera(px, py, pz, tx, ty, tz) {
        const c = controlsRef.current;
        if (!c) return;
        const dist = Math.hypot(px - tx, py - ty, pz - tz);
        if (dist < c.minDistance) c.minDistance = Math.max(0.28, dist * 0.85);
        c.object.position.set(px, py, pz);
        c.target.set(tx, ty, tz);
        c.update();
      },
      getCamera() {
        const controls = controlsRef.current;
        const cam = controls?.object;
        if (!cam || !controls) return null;
        const tx = controls.target.x;
        const ty = controls.target.y;
        const tz = controls.target.z;
        const px = cam.position.x;
        const py = cam.position.y;
        const pz = cam.position.z;
        const dx = px - tx;
        const dy = py - ty;
        const dz = pz - tz;
        return {
          x: px,
          y: py,
          z: pz,
          tx,
          ty,
          tz,
          azimuth: Math.atan2(dx, dz),
          polar: Math.atan2(Math.hypot(dx, dz), dy),
        };
      },
      setLedgerView(on) {
        setShowLedger(on);
        showLedgerRef.current = on;
        worldRef.current?.setDebug(on);
      },
    };
    return () => {
      delete window.__damageLabProbe;
      delete window.__damageLabState;
    };
  }, []);

  const weaponButtons = WEAPONS.map((kind) => (
    <button
      key={kind}
      type="button"
      data-testid={`weapon-${kind}`}
      className={weapon === kind ? 'is-on' : ''}
      aria-pressed={weapon === kind}
      onClick={() => {
        weaponRef.current = kind;
        setWeaponState(kind);
        writeState({ weapon: kind });
      }}
    >
      {kind}
    </button>
  ));

  const energyControl = (
    <label className="dl-row dl-energy-control">
      <span>Energy {energy.toFixed(2)}</span>
      <input
        data-testid="energy-slider"
        type="range"
        min={0}
        max={100}
        step={1}
        value={Math.round(energyToSlider(energy) * 100)}
        onChange={(e) => setEnergy(sliderToEnergy(Number(e.target.value) / 100))}
        aria-label="Stamp energy"
      />
    </label>
  );

  const ledgerToggle = (
    <button
      type="button"
      data-testid="toggle-ledger"
      className={`dl-toggle${showLedger ? ' is-on' : ''}`}
      aria-pressed={showLedger}
      onClick={() => {
        const next = !showLedger;
        setShowLedger(next);
        showLedgerRef.current = next;
        worldRef.current?.setDebug(next);
      }}
    >
      Ledger
    </button>
  );

  const resetButton = (
    <button
      type="button"
      data-testid="reset-lab"
      onClick={() => window.__damageLabProbe?.reset()}
    >
      Reset
    </button>
  );

  const apiToggle = (
    <button
      type="button"
      data-testid="toggle-api"
      className={`dl-toggle${apiOpen ? ' is-on' : ''}`}
      aria-pressed={apiOpen}
      onClick={() => setApiOpen((v) => !v)}
    >
      API
    </button>
  );

  const last = hud.lastCall;
  const apiSnippet = last
    ? `world.stamp({
  position: [${last.position.map((n) => n.toFixed(2)).join(', ')}],
  direction: [${last.direction.map((n) => n.toFixed(2)).join(', ')}],
  energy: ${last.energy.toFixed(2)},
  intensity: ${last.intensity.toFixed(2)},
  blastFraction: ${last.blastFraction.toFixed(2)},
  surfaceNormal: [${last.surfaceNormal.map((n) => n.toFixed(2)).join(', ')}],
  profile: getProfile('${last.profileId}'),
})`
    : `world.stamp({
  position: [x, y, z],
  direction: [dx, dy, dz],
  energy,
  intensity,
  blastFraction,
  surfaceNormal: [0, 0, 1],
  profile: getProfile('brick'),
})`;

  const materialButtons = DAMAGE_PROFILES.map((p) => (
    <button
      key={p.id}
      type="button"
      data-testid={`material-${p.id}`}
      className={p.id === 'brick' ? 'is-on' : ''}
      aria-pressed={p.id === 'brick'}
      disabled={p.isStub}
      title={p.isStub ? p.notes : p.label}
    >
      {p.label}
    </button>
  ));

  return (
    <div
      className={`damage-lab-page${isMobile ? ' is-mobile' : ''}`}
      data-testid="damage-lab-page"
    >
      {isMobile && (
        <header className="mobile-lab-appbar dl-appbar" data-testid="dl-appbar">
          <button type="button" className="dl-exit" onClick={onBack} data-testid="dl-back">
            ← Back
          </button>
          <h1 className="mobile-lab-appbar-title dl-appbar-title">DAMAGE LAB</h1>
          <button
            type="button"
            className="mobile-lab-appbar-status dl-live-stats"
            data-testid="dl-live-stats"
            aria-expanded={statsOpen}
            aria-label="Toggle full stats"
            onClick={() => setStatsOpen((v) => !v)}
          >
            {hud.removed} gone
          </button>
        </header>
      )}

      <div className="dl-stage" data-testid="dl-stage">
        <Canvas
          className="dl-canvas-root"
          dpr={1}
          camera={{
            position: [DEFAULT_CAM.px, DEFAULT_CAM.py, DEFAULT_CAM.pz],
            fov: 40,
            near: 0.08,
            far: 80,
          }}
          gl={{
            antialias: true,
            alpha: false,
            toneMapping: THREE.ACESFilmicToneMapping,
            toneMappingExposure: 1.06,
            powerPreference: 'high-performance',
          }}
          onCreated={({ gl, scene: sc }) => {
            gl.domElement.style.touchAction = 'none';
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.06;
            sc.fog = new THREE.Fog(0x0a0b0e, 18, 42);
            sc.background = new THREE.Color(0x08090c);
          }}
        >
          <LabScene
            worldRef={worldRef}
            energyRef={energyRef}
            weaponRef={weaponRef}
            controlsRef={controlsRef}
            ledgerCanvasRef={ledgerCanvasRef}
            showLedgerRef={showLedgerRef}
            onHud={setHud}
            onStamp={dismissHint}
          />
        </Canvas>

        {isMobile && hintVisible && (
          <div className="dl-toast" data-testid="dl-toast" role="status">
            tap to stamp · drag to orbit
          </div>
        )}

        {isMobile && statsOpen && (
          <div className="dl-stats-overlay" data-testid="damage-hud">
            <div>
              removed <b>{hud.removed}</b>
            </div>
            <div>
              rim <b>{hud.rim}</b>
            </div>
            <div>
              fps <b>{hud.fps.toFixed(0)}</b>
            </div>
            <div>stamps {hud.stamps}</div>
            <div data-testid="hud-weapon">
              weapon <b>{weapon}</b>
            </div>
          </div>
        )}

        {!isMobile && (
          <div className="dl-overlay">
            <div className="dl-brand">
              <button className="dl-exit" type="button" onClick={onBack}>
                ← Lab
              </button>
              <h1 className="dl-title">Damage Lab</h1>
              <p className="dl-sub">Phase 3 — weapons, profiles, API</p>
            </div>

            <aside className="dl-panel">
              <h2>Material</h2>
              <div className="dl-mats">{materialButtons}</div>
              <h2>Weapon</h2>
              <div className="dl-mats dl-weapons">{weaponButtons}</div>
              {energyControl}
              <div className="dl-actions">
                {resetButton}
                {ledgerToggle}
              </div>
              <div className="dl-actions">{apiToggle}</div>
            </aside>

            {apiOpen && (
              <pre className="dl-api" data-testid="api-panel">
                {apiSnippet}
              </pre>
            )}

            <div className="dl-hud" data-testid="damage-hud">
              <div>
                removed <b>{hud.removed}</b>
              </div>
              <div>
                rim <b>{hud.rim}</b>
              </div>
              <div>
                cracked <b>{hud.cracked}</b>
              </div>
              <div>
                stains <b>{hud.stains}</b>
              </div>
              <div>
                scorch <b>{hud.scorch}</b>
              </div>
              <div>
                debris <b>{hud.debris}</b>
              </div>
              <div>
                back <b>{hud.backFace}</b>
              </div>
              <div>
                fps <b>{hud.fps.toFixed(0)}</b>
              </div>
              <div>stamps {hud.stamps}</div>
              <div data-testid="hud-weapon">
                weapon <b>{weapon}</b>
              </div>
            </div>

            <div className="dl-hint">click to stamp · drag to orbit</div>

            {showLedger && (
              <div className="dl-ledger" data-testid="dl-ledger-inset">
                <canvas ref={ledgerCanvasRef} width={280} height={168} />
              </div>
            )}
          </div>
        )}
      </div>

      {isMobile && (
        <div className="mobile-lab-dock dl-dock" data-testid="dl-dock">
          <div className="dl-dock-materials dl-dock-weapons" data-testid="weapon-selector">
            {weaponButtons}
          </div>
          <div className="dl-dock-controls">
            {energyControl}
            {resetButton}
            {ledgerToggle}
          </div>
          {showLedger && (
            <div className="dl-ledger dl-ledger-dock" data-testid="dl-ledger-inset">
              <canvas ref={ledgerCanvasRef} width={200} height={120} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
